import React from "react";
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  BrainCircuit,
  Building2,
  Bot,
  BriefcaseBusiness,
  CalendarClock,
  ChartNoAxesCombined,
  CircleDollarSign,
  Compass,
  Globe,
  Hammer,
  Handshake,
  Layers,
  Lightbulb,
  LineChart,
  Package,
  Plane,
  Rocket,
  Settings2,
  ShieldCheck,
  Sparkles,
  Target,
  Trophy,
  TrendingUp,
  Wrench,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  LineChart as RechartsLineChart,
} from "recharts";
import "./ZoomOnePagerPage.css";

function SectionCard({ icon: Icon, title, children, accent = "blue" }) {
  return (
    <section className={`zoom-one-pager__section zoom-one-pager__section--${accent}`}>
      <header className="zoom-one-pager__section-header">
        <span className="zoom-one-pager__icon-wrap">
          <Icon size={18} strokeWidth={2.2} />
        </span>
        <h2>{title}</h2>
      </header>
      <div className="zoom-one-pager__section-body">{children}</div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="zoom-one-pager__bullet">
      <BadgeCheck size={15} strokeWidth={2.5} />
      <span>{children}</span>
    </li>
  );
}

function IconLine({ icon: Icon, children, tone = "blue" }) {
  const toneClass = tone === "orange" ? "tw-text-orange-500" : "tw-text-blue-500";
  return (
    <li className="tw-flex tw-items-start tw-gap-2 tw-text-sm tw-text-slate-700">
      <Icon size={14} className={toneClass} />
      <span>{children}</span>
    </li>
  );
}

const growthTimeline = [
  {
    year: "2023",
    stage: "התחלה",
    tone: "past",
    icon: Hammer,
    milestones: [
      "הקמת HotelsRiver",
      "בניית תשתית ראשונית",
      "התחלת פיתוח מערכת",
      "עבודה ידנית + ניסוי שוק",
    ],
  },
  {
    year: "2024",
    stage: "בנייה",
    tone: "progress",
    icon: Settings2,
    milestones: [
      "פיתוח מערכת סוכנים (Dashboard)",
      "חיבור לספקים",
      "התחלת פעילות עם סוכנים",
      "יצירת הכנסות ראשונות",
    ],
  },
  {
    year: "2025",
    stage: "מוצר עובד",
    tone: "progress",
    icon: BarChart3,
    milestones: [
      "מנוע חבילות (LDS)",
      "מערכת הצעות מחיר",
      "מערכת תשלומים",
      "עשרות סוכנים פעילים",
      "מחזור ~5M ₪",
    ],
  },
  {
    year: "2026",
    stage: "סקייל",
    tone: "current",
    icon: Rocket,
    milestones: [
      "אתר פומבי (B2C)",
      "חבילות משולבות טיסות",
      "UX/UI מתקדם",
      "אוטומציות מכירה",
      "מעבר למודל בתשלום",
    ],
  },
];

const growthChartData = [
  { year: "2023", value: 0, label: "0" },
  { year: "2024", value: 1, label: "1M" },
  { year: "2025", value: 7, label: "7M+" },
  { year: "2026", value: 9, label: "Growth" },
];

function GrowthLineChartBlock() {
  return (
    <section className="zoom-one-pager__section zoom-one-pager__section--blue">
      <header className="zoom-one-pager__section-header">
        <span className="zoom-one-pager__icon-wrap">
          <LineChart size={18} strokeWidth={2.2} />
        </span>
        <h2>צמיחת החברה</h2>
      </header>

      <div className="zoom-one-pager__chart-wrap">
        <ResponsiveContainer width="100%" height={240}>
          <RechartsLineChart
            data={growthChartData}
            margin={{ top: 16, right: 10, left: -16, bottom: 6 }}
          >
            <defs>
              <linearGradient id="growthStroke" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="75%" stopColor="#60a5fa" />
                <stop offset="100%" stopColor="#f97316" />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" vertical={false} />
            <XAxis dataKey="year" tick={{ fill: "#64748b", fontSize: 12 }} tickLine={false} axisLine={false} />
            <YAxis hide domain={[0, 10]} />
            <Tooltip
              cursor={{ stroke: "#cbd5e1", strokeWidth: 1 }}
              contentStyle={{
                borderRadius: "10px",
                border: "1px solid #dbe6f3",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                fontSize: "12px",
              }}
              formatter={(_, __, item) => [item?.payload?.label, "Value"]}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="url(#growthStroke)"
              strokeWidth={4}
              dot={(props) => {
                const isCurrent = props?.payload?.year === "2026";
                return (
                  <circle
                    cx={props.cx}
                    cy={props.cy}
                    r={isCurrent ? 7 : 4}
                    fill={isCurrent ? "#f97316" : "#3b82f6"}
                    className={isCurrent ? "zoom-one-pager__chart-dot-current" : ""}
                  />
                );
              }}
              activeDot={{ r: 8, fill: "#f97316" }}
              isAnimationActive
              animationDuration={1700}
              animationEasing="ease-out"
            />
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>

      <div className="zoom-one-pager__kpi-wow">
        <div className="zoom-one-pager__kpi-wow-card">
          <span>💰</span>
          <strong>7M+ ₪</strong>
          <p>מחזור</p>
        </div>
        <div className="zoom-one-pager__kpi-wow-card">
          <span>👥</span>
          <strong>עשרות</strong>
          <p>סוכנים פעילים</p>
        </div>
        <div className="zoom-one-pager__kpi-wow-card">
          <span>🚀</span>
          <strong>LIVE</strong>
          <p>מוצר פעיל</p>
        </div>
      </div>
    </section>
  );
}

function TimelineStageCard({ item, index }) {
  const Icon = item.icon;
  const stageStyles = {
    past: {
      card: "tw-border-slate-200 tw-bg-slate-50/70 hover:tw-bg-slate-50",
      badge: "tw-bg-slate-200 tw-text-slate-700",
      dot: "timeline-dot timeline-dot--past",
      icon: "tw-text-slate-600",
    },
    progress: {
      card: "tw-border-blue-200 tw-bg-blue-50/70 hover:tw-bg-blue-50",
      badge: "tw-bg-blue-100 tw-text-blue-700",
      dot: "timeline-dot timeline-dot--progress",
      icon: "tw-text-blue-600",
    },
    current: {
      card: "tw-border-orange-300 tw-bg-orange-50/80 hover:tw-bg-orange-50 tw-scale-[1.05] tw-ring-1 tw-ring-orange-200 tw-shadow-[0_14px_32px_rgba(249,115,22,0.22)]",
      badge: "tw-bg-orange-100 tw-text-orange-700",
      dot: "timeline-dot timeline-dot--current",
      icon: "tw-text-orange-600",
    },
  };
  const style = stageStyles[item.tone];

  return (
    <div
      className={`timeline-appear tw-relative tw-flex-1 tw-rounded-2xl tw-border tw-p-4 tw-transition-all tw-duration-300 hover:tw--translate-y-1 hover:tw-shadow-xl hover:tw-ring-2 hover:tw-ring-blue-100 ${style.card}`}
      style={{ animationDelay: `${index * 120}ms` }}
    >
      <span className={`tw-absolute md:tw-top-5 md:tw-left-1/2 md:tw--translate-x-1/2 tw-top-5 tw-right-[-30px] ${style.dot}`} />
      <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-gap-2">
        <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-px-2.5 tw-py-1 tw-text-xs tw-font-extrabold ${style.badge}`}>
          <CalendarClock size={13} />
          {item.year}
        </span>
        <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-bold ${style.icon}`}>
          <Icon size={15} />
          {item.stage}
        </span>
      </div>
      <ul className="tw-space-y-2">
        {item.milestones.map((milestone) => (
          <li key={`${item.year}-${milestone}`} className="tw-flex tw-items-start tw-gap-2 tw-text-sm tw-text-slate-700">
            <BadgeCheck size={14} className={item.tone === "current" ? "tw-text-orange-500" : item.tone === "progress" ? "tw-text-blue-500" : "tw-text-slate-400"} />
            <span>{milestone}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function GrowthTimeline() {
  return (
    <section className="zoom-one-pager__section zoom-one-pager__section--blue">
      <header className="zoom-one-pager__section-header">
        <span className="zoom-one-pager__icon-wrap">
          <TrendingUp size={18} strokeWidth={2.2} />
        </span>
        <h2>Growth Timeline | 2023-2026</h2>
      </header>

      <div className="tw-relative tw-mt-4">
        <div className="tw-hidden md:tw-block tw-pointer-events-none tw-absolute tw-left-10 tw-right-10 tw-top-6 tw-h-px tw-bg-slate-200" />
        <div className="md:tw-hidden tw-pointer-events-none tw-absolute tw-right-6 tw-top-0 tw-bottom-0 tw-w-px tw-bg-slate-200" />

        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-3 md:tw-gap-4">
          {growthTimeline.map((item, index) => (
            <TimelineStageCard key={item.year} item={item} index={index} />
          ))}
        </div>
      </div>

      <p className="tw-mt-4 tw-text-sm tw-font-extrabold tw-text-slate-900">
        זה כבר עסק עובד - לא ניסוי
      </p>
      <p className="tw-mt-2 tw-text-sm tw-font-bold tw-text-blue-700">
        ב-3 שנים - ממערכת בסיסית למוצר שעובד ומייצר מחזור
      </p>
    </section>
  );
}

export default function ZoomOnePagerPage() {
  return (
    <main className="zoom-one-pager" dir="rtl">
      <div className="zoom-one-pager__shell">
        <section className="zoom-one-pager__hero">
          <p className="zoom-one-pager__eyebrow">HotelsRiver | Zoom Agents 2026</p>
          <h1>HotelsRiver - עושים סדר, מתקדמים קדימה</h1>
          <p className="zoom-one-pager__subtitle">עברנו שלב. עכשיו זה עסק אמיתי.</p>
          <p className="zoom-one-pager__cta">
            <Sparkles size={16} strokeWidth={2.4} />
            <span>הזום היום - החלטה עד חמישי</span>
          </p>
        </section>

        <SectionCard icon={BriefcaseBusiness} title="מי אנחנו" accent="orange">
          <p>
            HotelsRiver היא פלטפורמת TravelTech. לא סוכנות - מערכת מלאה לסוכנים.
          </p>
          <ul>
            <IconLine icon={Plane} tone="orange">טיסות</IconLine>
            <IconLine icon={Building2} tone="orange">מלונות</IconLine>
            <IconLine icon={Package} tone="orange">חבילות נופש</IconLine>
            <IconLine icon={Sparkles} tone="orange">הצעות מותאמות אישית</IconLine>
          </ul>
          <p className="zoom-one-pager__line">נותנים לכל סוכן כוח של משרד נסיעות.</p>
        </SectionCard>

        <SectionCard icon={ShieldCheck} title="מה כבר קיים (Proof)">
          <ul>
            <IconLine icon={Layers}>מערכת סוכנים מלאה</IconLine>
            <IconLine icon={Globe}>אתר פומבי</IconLine>
            <IconLine icon={Package}>מנוע חבילות (LDS)</IconLine>
            <IconLine icon={Handshake}>חיבור לספקים</IconLine>
            <IconLine icon={CircleDollarSign}>מערכת תשלומים</IconLine>
          </ul>
          <div className="zoom-one-pager__kpis">
            <div>
              <strong>~5M ₪</strong>
              <span>מחזור</span>
            </div>
            <div>
              <strong>עשרות</strong>
              <span>סוכנים פעילים</span>
            </div>
            <div>
              <strong>LIVE</strong>
              <span>מוצר עובד</span>
            </div>
          </div>
        </SectionCard>

        <SectionCard icon={Trophy} title="מובילים בשוק" accent="orange">
          <p>
            <strong>היינו הראשונים להביא:</strong>
          </p>
          <ul className="tw-space-y-2">
            <li className="tw-text-sm tw-text-slate-700">✔ מערכת חבילות דינמיות (LDS) לסוכנים פרילנסרים</li>
            <li className="tw-text-sm tw-text-slate-700">✔ מלון כשר בסלוניקי לשוק הישראלי</li>
            <li className="tw-text-sm tw-text-slate-700">✔ שיתוף פעולה עם מנו ספנות לסוכנים עצמאיים</li>
          </ul>
          <p className="zoom-one-pager__line">אנחנו לא מגיבים לשוק - אנחנו בונים אותו.</p>
        </SectionCard>

        <GrowthTimeline />
        <GrowthLineChartBlock />

        <SectionCard icon={Rocket} title="חדשנות 2026">
          <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-blue-200 tw-bg-gradient-to-r tw-from-blue-50 tw-to-white tw-p-3">
            <div className="tw-flex tw-items-center tw-gap-2">
              <BrainCircuit size={28} className="tw-text-blue-600" />
              <span className="tw-text-sm tw-font-extrabold tw-text-slate-900">AI Powered</span>
            </div>
            <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-orange-100 tw-px-2 tw-py-1 tw-text-[11px] tw-font-extrabold tw-text-orange-700">
              NEW
            </span>
          </div>
          <p>
            <strong>ממשיכים להוביל את השוק:</strong>
          </p>
          <ul className="tw-space-y-2">
            <IconLine icon={BrainCircuit}>סוכן AI אישי לכל סוכן</IconLine>
            <IconLine icon={LineChart}>חבילת מניות לסוכנים</IconLine>
            <IconLine icon={Settings2}>שדרוג מערכות המכירה והאוטומציה</IconLine>
          </ul>
        </SectionCard>

        <section className="zoom-one-pager__section zoom-one-pager__section--blue">
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <div className="tw-rounded-xl tw-border tw-border-blue-100 tw-bg-blue-50/60 tw-p-4">
              <header className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
                <h3 className="tw-text-base tw-font-extrabold tw-text-slate-900">סוכן AI אישי</h3>
                <Bot size={16} className="tw-text-blue-600" />
              </header>
              <ul className="tw-space-y-1.5">
                <IconLine icon={Sparkles}>יצירת הצעות מחיר במהירות</IconLine>
                <IconLine icon={Bot}>מענה ראשוני ללקוחות</IconLine>
                <IconLine icon={Wrench}>חיסכון בזמן תפעולי</IconLine>
                <IconLine icon={TrendingUp}>שיפור אחוזי סגירה</IconLine>
              </ul>
              <p className="tw-mt-2 tw-text-sm tw-font-bold tw-text-blue-700">המטרה: יותר מכירות בפחות זמן</p>
            </div>

            <div className="tw-rounded-xl tw-border tw-border-blue-100 tw-bg-blue-50/60 tw-p-4">
              <header className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
                <h3 className="tw-text-base tw-font-extrabold tw-text-slate-900">חבילת מניות לסוכנים</h3>
                <LineChart size={16} className="tw-text-blue-600" />
              </header>
              <ul className="tw-space-y-1.5">
                <IconLine icon={TrendingUp}>השתתפות בצמיחה של HotelsRiver</IconLine>
                <IconLine icon={Handshake}>אינטרס משותף לטווח ארוך</IconLine>
                <IconLine icon={Target}>חיזוק מחויבות מקצועית</IconLine>
              </ul>
              <p className="tw-mt-2 tw-text-sm tw-font-bold tw-text-blue-700">
                הסוכן לא רק עובד במערכת - הוא חלק מהצמיחה
              </p>
            </div>
          </div>
        </section>

        <SectionCard icon={Wrench} title="מה השתדרג במערכת" accent="orange">
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
            <div className="tw-rounded-xl tw-border tw-border-orange-100 tw-bg-orange-50/60 tw-p-3">
              <p className="tw-mb-1 tw-text-sm tw-font-extrabold tw-text-slate-900">✔ מנוע חבילות (LDS) מתקדם</p>
              <p className="tw-text-sm tw-text-slate-700">שילוב טיסות + מלונות</p>
              <p className="tw-text-sm tw-text-slate-700">תמחור דינמי מדויק</p>
            </div>
            <div className="tw-rounded-xl tw-border tw-border-orange-100 tw-bg-orange-50/60 tw-p-3">
              <p className="tw-mb-1 tw-text-sm tw-font-extrabold tw-text-slate-900">✔ אתר פומבי לסוכן (B2C)</p>
              <p className="tw-text-sm tw-text-slate-700">אתר אישי לכל סוכן</p>
              <p className="tw-text-sm tw-text-slate-700">מכירה ישירה ללקוח</p>
            </div>
            <div className="tw-rounded-xl tw-border tw-border-orange-100 tw-bg-orange-50/60 tw-p-3">
              <p className="tw-mb-1 tw-text-sm tw-font-extrabold tw-text-slate-900">✔ UX/UI משופר</p>
              <p className="tw-text-sm tw-text-slate-700">עבודה מהירה יותר</p>
              <p className="tw-text-sm tw-text-slate-700">פחות טעויות</p>
              <p className="tw-text-sm tw-text-slate-700">חוויית שימוש טובה יותר</p>
            </div>
            <div className="tw-rounded-xl tw-border tw-border-orange-100 tw-bg-orange-50/60 tw-p-3">
              <p className="tw-mb-1 tw-text-sm tw-font-extrabold tw-text-slate-900">✔ אוטומציות מכירה</p>
              <p className="tw-text-sm tw-text-slate-700">תהליכים חכמים</p>
              <p className="tw-text-sm tw-text-slate-700">חיסכון בזמן</p>
              <p className="tw-text-sm tw-text-slate-700">עבודה מסודרת</p>
            </div>
          </div>
          <p className="zoom-one-pager__line">אנחנו בונים מערכת שעובדת בשביל הסוכן - לא להפך.</p>
        </SectionCard>

        <SectionCard icon={Layers} title="מה השתנה" accent="orange">
          <ul>
            <Bullet>חבילות משולבות טיסות</Bullet>
            <Bullet>אתר פומבי לסוכנים</Bullet>
            <Bullet>UX/UI מתקדם</Bullet>
            <Bullet>אוטומציות מכירה</Bullet>
          </ul>
          <p className="zoom-one-pager__line">זה כבר לא ניסוי - זה מוצר.</p>
        </SectionCard>

        <SectionCard icon={Target} title="לאן הולכים">
          <div className="zoom-one-pager__targets">
            <span>
              <TrendingUp size={15} /> 100-300 סוכנים פעילים
            </span>
            <span>
              <Globe size={15} /> כניסה לארה"ב
            </span>
            <span>
              <Handshake size={15} /> הסכמים ישירים
            </span>
            <span>
              <ChartNoAxesCombined size={15} /> עשרות מיליונים מחזור
            </span>
          </div>
        </SectionCard>

        <SectionCard icon={Compass} title="הבעיה (חד וברור)" accent="orange">
          <ul>
            <Bullet>יותר מדי משתמשים לא פעילים</Bullet>
            <Bullet>חוסר מחויבות</Bullet>
            <Bullet>עומס בלי החזר</Bullet>
          </ul>
          <p className="zoom-one-pager__line">זה עוצר צמיחה.</p>
        </SectionCard>

        <SectionCard icon={CircleDollarSign} title="מודל העבודה - מעבר לתשלום" accent="orange">
          <p>המערכת עברה משלב פיתוח לשלב פעילות עסקית מלאה.</p>
          <p>כדי להמשיך להתפתח ולספק שירות ברמה גבוהה, נדרש מודל יציב וברור.</p>
          <ul className="tw-space-y-1.5">
            <IconLine icon={Wrench} tone="orange">פיתוח מתמשך של המערכת</IconLine>
            <IconLine icon={Handshake} tone="orange">תפעול ושירות שוטף</IconLine>
            <IconLine icon={Globe} tone="orange">הרחבת ספקים ושיתופי פעולה</IconLine>
            <IconLine icon={TrendingUp} tone="orange">שיווק והבאת לקוחות</IconLine>
          </ul>
          <p className="zoom-one-pager__line">מודל חינמי לא מאפשר יציבות וצמיחה לאורך זמן.</p>

          <div className="tw-mt-4 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
            <div className="tw-rounded-xl tw-border tw-border-blue-100 tw-bg-blue-50/60 tw-p-3">
              <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-extrabold tw-text-slate-900">
                <Target size={14} className="tw-text-blue-600" />
                מחויבות לעבודה
              </p>
              <p className="tw-mt-1 tw-text-sm tw-text-slate-700">סוכן שמשלם עובד בצורה עקבית ומקצועית.</p>
            </div>
            <div className="tw-rounded-xl tw-border tw-border-blue-100 tw-bg-blue-50/60 tw-p-3">
              <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-extrabold tw-text-slate-900">
                <TrendingUp size={14} className="tw-text-blue-600" />
                יותר פעילות = יותר הכנסות
              </p>
              <p className="tw-mt-1 tw-text-sm tw-text-slate-700">יותר שימוש - יותר הצעות - יותר סגירות.</p>
            </div>
            <div className="tw-rounded-xl tw-border tw-border-blue-100 tw-bg-blue-50/60 tw-p-3">
              <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-extrabold tw-text-slate-900">
                <Wrench size={14} className="tw-text-blue-600" />
                מערכת שמתפתחת
              </p>
              <p className="tw-mt-1 tw-text-sm tw-text-slate-700">השקעה חוזרת בפיתוח ושיפור הכלים.</p>
            </div>
            <div className="tw-rounded-xl tw-border tw-border-blue-100 tw-bg-blue-50/60 tw-p-3">
              <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-extrabold tw-text-slate-900">
                <Handshake size={14} className="tw-text-blue-600" />
                סביבה מקצועית
              </p>
              <p className="tw-mt-1 tw-text-sm tw-text-slate-700">עבודה עם סוכנים פעילים בלבד.</p>
            </div>
          </div>

          <p className="zoom-one-pager__line">המודל נועד לייצר מערכת יציבה, מתקדמת ורווחית למי שעובד בה.</p>
        </SectionCard>

        <SectionCard icon={Rocket} title="ההחלטה העסקית">
          <p>המערכת עוברת למודל בתשלום.</p>
          <ul>
            <IconLine icon={Wrench}>פיתוח</IconLine>
            <IconLine icon={Settings2}>תפעול</IconLine>
            <IconLine icon={Handshake}>שירות</IconLine>
            <IconLine icon={TrendingUp}>שיווק</IconLine>
          </ul>
          <p className="zoom-one-pager__line">בלי זה - אין חברה.</p>
        </SectionCard>

        <SectionCard icon={ChartNoAxesCombined} title="המשמעות" accent="orange">
          <p>מי שמשלם:</p>
          <ul>
            <IconLine icon={Target} tone="orange">עובד יותר</IconLine>
            <IconLine icon={TrendingUp} tone="orange">מוכר יותר</IconLine>
            <IconLine icon={CircleDollarSign} tone="orange">מרוויח יותר</IconLine>
          </ul>
          <p className="zoom-one-pager__line">זה מסנן רציניים.</p>
        </SectionCard>

        <SectionCard icon={ArrowRight} title="הבחירה">
          <div className="zoom-one-pager__choice">
            <div>
              <h3>נשארים</h3>
              <p>מתקדמים ומרוויחים.</p>
            </div>
            <div>
              <h3>יוצאים</h3>
              <p>נפרדים.</p>
            </div>
          </div>
          <p className="zoom-one-pager__line zoom-one-pager__line--strong">אין באמצע.</p>
        </SectionCard>

        <section className="zoom-one-pager__finale">
          <h2>אנחנו בנקודת מעבר</h2>
          <div className="zoom-one-pager__finale-points">
            <span>
              <Plane size={15} /> יש מוצר
            </span>
            <span>
              <Globe size={15} /> יש שוק
            </span>
            <span>
              <TrendingUp size={15} /> יש הזדמנות
            </span>
          </div>
          <p>עכשיו זה תלוי בכם.</p>
        </section>
      </div>
    </main>
  );
}
